/**
 * `/docs/sdks` — install + first-call samples per supported SDK.
 *
 * No official SDKs ship today. We render placeholders for Node,
 * Python, and Go with "coming soon" copy plus a fallback "roll your
 * own from the OpenAPI spec" card pointing at /docs/api. Stage 5
 * handover tracks this.
 */
import { CodeSnippet } from '@/components/primitives/CodeSnippet';
import { SdkInstallBlock } from '@/components/primitives/SdkInstallBlock';
import { Island } from '@/components/shell/Island';
import { Button } from '@/components/ui/button';
import { Code, Package } from '@phosphor-icons/react';
import { Link, createFileRoute } from '@tanstack/react-router';
import type { ReactElement } from 'react';

export const Route = createFileRoute('/_app/docs/sdks')({
  component: SdksDocs,
});

interface SdkEntry {
  id: 'node' | 'python' | 'go';
  name: string;
  status: 'real' | 'coming_soon';
  commands: Parameters<typeof SdkInstallBlock>[0]['commands'];
  firstCall: string;
}

const SDKS: SdkEntry[] = [
  {
    id: 'node',
    name: 'Node / TypeScript',
    status: 'coming_soon',
    commands: [
      { id: 'pnpm', label: 'pnpm', command: 'pnpm add @clickweave/sdk' },
      { id: 'npm', label: 'npm', command: 'npm install @clickweave/sdk' },
      { id: 'yarn', label: 'yarn', command: 'yarn add @clickweave/sdk' },
      { id: 'bun', label: 'bun', command: 'bun add @clickweave/sdk' },
    ],
    firstCall:
      "import { Clickweave } from '@clickweave/sdk';\n\n" +
      'const wv = new Clickweave({ apiKey: process.env.CLICKWEAVE_API_KEY });\n' +
      "const audit = await wv.audits.create({ url: 'https://example.com' });\n" +
      'console.log(audit.id);',
  },
  {
    id: 'python',
    name: 'Python',
    status: 'coming_soon',
    commands: [{ id: 'pip', label: 'pip', command: 'pip install clickweave' }],
    firstCall:
      'import os\n' +
      'from clickweave import Clickweave\n\n' +
      "wv = Clickweave(api_key=os.environ['CLICKWEAVE_API_KEY'])\n" +
      "audit = wv.audits.create(url='https://example.com')\n" +
      'print(audit.id)',
  },
  {
    id: 'go',
    name: 'Go',
    status: 'coming_soon',
    commands: [{ id: 'go', label: 'go get', command: 'go get github.com/clickweave/go-sdk' }],
    firstCall:
      'import "github.com/clickweave/go-sdk"\n\n' +
      'client := clickweave.NewClient(os.Getenv("CLICKWEAVE_API_KEY"))\n' +
      'audit, err := client.Audits.Create(ctx, clickweave.AuditRequest{URL: "https://example.com"})',
  },
];

function SdksDocs(): ReactElement {
  const anyReal = SDKS.some((s) => s.status === 'real');

  return (
    <Island breadcrumb={[{ label: 'Docs', href: '/docs' }, { label: 'SDKs' }]}>
      <div className="mx-auto max-w-3xl space-y-8">
        <header className="space-y-2">
          <h1 className="text-2xl font-semibold text-[var(--color-ink)]">Official SDKs</h1>
          <p className="text-sm text-[var(--color-mute)]">
            Thin wrappers over the REST API with typed responses and retries. All SDKs speak the
            same authentication and error format as the raw HTTP endpoints.
          </p>
        </header>

        {!anyReal && (
          <section className="rounded-2xl border border-[var(--color-accent-brand)]/40 bg-[var(--color-accent-brand)]/5 p-5">
            <div className="flex items-start gap-3">
              <Code
                aria-hidden="true"
                size={24}
                weight="duotone"
                className="text-[var(--color-accent-brand)]"
              />
              <div className="flex-1 space-y-2">
                <h2 className="text-base font-semibold text-[var(--color-ink)]">
                  No official SDK yet? Generate one.
                </h2>
                <p className="text-sm text-[var(--color-mute)]">
                  Our OpenAPI document covers every public endpoint. Feed it to any generator
                  (openapi-typescript, openapi-python-client, oapi- codegen) to get a typed client
                  for your language of choice.
                </p>
                <Button variant="outline" size="sm" render={<Link to="/docs/api" />}>
                  See the API reference →
                </Button>
              </div>
            </div>
          </section>
        )}

        <div className="space-y-6">
          {SDKS.map((sdk) => (
            <section
              key={sdk.id}
              className="space-y-4 rounded-2xl border border-[var(--color-line)] bg-[var(--color-paper)] p-5"
            >
              <header className="flex items-start justify-between gap-3">
                <div className="flex items-center gap-2">
                  <Package
                    aria-hidden="true"
                    size={20}
                    weight="duotone"
                    className="text-[var(--color-accent-brand)]"
                  />
                  <h3 className="text-base font-semibold text-[var(--color-ink)]">{sdk.name}</h3>
                </div>
                {sdk.status === 'coming_soon' && (
                  <span className="rounded-full bg-[var(--color-paper-alt)] px-2 py-0.5 text-xs font-medium text-[var(--color-ink-soft)]">
                    Coming soon
                  </span>
                )}
              </header>
              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  Install
                </div>
                <SdkInstallBlock commands={sdk.commands} />
              </div>
              <div className="space-y-2">
                <div className="text-xs font-medium uppercase tracking-[0.06em] text-[var(--color-mute)]">
                  First call
                </div>
                <CodeSnippet code={sdk.firstCall} />
              </div>
            </section>
          ))}
        </div>
      </div>
    </Island>
  );
}
